/* eslint-disable no-restricted-syntax -- #2244 asks the gutter cards
   to stay white regardless of theme (same as the retired popover) so
   the body text reads consistently against them. Theme tokens would
   re-skin the surface. */

/**
 * One positioned card inside the comments gutter column (#2244).
 *
 * Draws a thin connector from the anchor's y-position to the card's
 * top when the stacking math had to push the card past its natural
 * anchor — per the ticket "the later one moves down until it clears,
 * with a connector to its anchor". Non-pushed cards render without
 * the connector so the common case stays visually clean.
 *
 * Extracted from `comments-gutter.tsx` so the gutter file stays under
 * the 300-line budget.
 */

import type { ReactNode } from 'react';

interface GutterSlotProps {
  top: number;
  anchorTop: number;
  pushed: boolean;
  children: ReactNode;
}

export function GutterSlot({
  top,
  anchorTop,
  pushed,
  children
}: GutterSlotProps) {
  // The slot wraps the card so the card itself stays a plain block.
  // `pointer-events-auto` wins the gutter's `pointer-events: none` so
  // clicks on the card still land — the outer `none` is so clicks on
  // the empty strip between cards fall through to the editor text.
  return (
    <div
      style={{
        position: 'absolute',
        top,
        right: 8,
        width: 'calc(100% - 16px)',
        pointerEvents: 'auto'
      }}
    >
      {pushed && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            // Draw the connector from the anchor's y (upper) down to
            // the card's top (lower). `top` is the slot's top (= card
            // top), `anchorTop` is where the paragraph sits.
            top: anchorTop - top,
            left: -8,
            width: 8,
            height: top - anchorTop,
            borderLeft: '1px dashed rgba(0,0,0,0.25)'
          }}
        />
      )}
      <div
        style={{
          backgroundColor: '#ffffff',
          color: '#111827',
          border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: 12,
          boxShadow:
            '0 10px 24px -8px rgba(0,0,0,0.12), 0 2px 6px -2px rgba(0,0,0,0.06)',
          overflow: 'hidden'
        }}
      >
        {children}
      </div>
    </div>
  );
}
